<template>
  <div class="auth-page">
    <div class="auth-card">
      <div class="auth-header">
        <i class="el-icon-collection-tag auth-icon"></i>
        <h1>{{ $t('system.name') }}</h1>
        <p>{{ isEn ? 'User Registration' : '普通用户注册' }}</p>
      </div>

      <el-form
        ref="registerForm"
        :model="form"
        :rules="rules"
        class="auth-form"
        @submit.native.prevent="handleRegister"
      >
        <el-form-item>
          <el-radio-group v-model="registerType" class="register-type-switch">
            <!-- Temporarily disabled: account and phone registration. -->
            <!-- <el-radio-button label="username">{{ isEn ? 'Account Register' : '账号注册' }}</el-radio-button> -->
            <!-- <el-radio-button label="phone">{{ isEn ? 'Phone Register' : '手机号注册' }}</el-radio-button> -->
            <el-radio-button label="email">{{ isEn ? 'Email Register' : '邮箱注册' }}</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item prop="identifier">
          <el-input
            v-model.trim="form.identifier"
            :prefix-icon="registerIcon"
            :placeholder="registerPlaceholder"
            clearable
          />
        </el-form-item>

        <el-form-item v-if="needsCode" prop="verificationCode">
          <div class="code-row">
            <el-input
              v-model.trim="form.verificationCode"
              prefix-icon="el-icon-key"
              :placeholder="isEn ? 'Enter verification code' : '请输入验证码'"
              clearable
            />
            <el-button
              class="code-button"
              :loading="sendingCode"
              :disabled="codeCountdown > 0"
              @click="sendCode"
            >
              {{ codeCountdown > 0 ? `${codeCountdown}s` : (isEn ? 'Send Code' : '发送验证码') }}
            </el-button>
          </div>
        </el-form-item>

        <el-form-item prop="password">
          <el-input
            v-model="form.password"
            prefix-icon="el-icon-lock"
            type="password"
            :placeholder="isEn ? 'Enter password (at least 6 characters)' : '请输入密码（至少 6 位）'"
            show-password
          />
        </el-form-item>

        <el-form-item prop="confirmPassword">
          <el-input
            v-model="form.confirmPassword"
            prefix-icon="el-icon-lock"
            type="password"
            :placeholder="isEn ? 'Confirm password' : '请再次输入密码'"
            show-password
            @keyup.enter.native="handleRegister"
          />
        </el-form-item>

        <el-button
          type="primary"
          class="submit-button"
          :loading="loading"
          @click="handleRegister"
        >
          {{ loading ? (isEn ? 'Registering...' : '注册中...') : (isEn ? 'Register' : '注册') }}
        </el-button>
      </el-form>

      <div class="back-login">
        {{ isEn ? 'Already have an account?' : '已有账号？' }}
        <a href="javascript:void(0)" @click="$router.push('/login')">{{ isEn ? 'Back to Login' : '返回登录' }}</a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Register',
  data() {
    const validateConfirm = (rule, value, callback) => {
      if (value !== this.form.password) {
        callback(new Error(this.isEn ? 'Passwords do not match' : '两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    return {
      loading: false,
      sendingCode: false,
      codeCountdown: 0,
      codeTimer: null,
      registerType: 'email',
      form: {
        identifier: '',
        verificationCode: '',
        password: '',
        confirmPassword: ''
      },
      rules: {
        identifier: [
          { required: true, message: this.$i18n.locale === 'en' ? 'Enter registration information' : '请输入注册信息', trigger: 'blur' },
          { validator: this.validateIdentifier, trigger: 'blur' }
        ],
        verificationCode: [
          { validator: this.validateVerificationCode, trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.$i18n.locale === 'en' ? 'Enter password' : '请输入密码', trigger: 'blur' },
          { min: 6, max: 128, message: this.$i18n.locale === 'en' ? 'Password must be at least 6 characters' : '密码至少 6 位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: this.$i18n.locale === 'en' ? 'Confirm password' : '请再次输入密码', trigger: 'blur' },
          { validator: validateConfirm, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    isEn() {
      return this.$i18n.locale === 'en'
    },
    needsCode() {
      return this.registerType === 'phone' || this.registerType === 'email'
    },
    registerPlaceholder() {
      return {
        username: this.isEn ? 'Enter account' : '请输入账号',
        phone: this.isEn ? 'Enter phone number' : '请输入手机号',
        email: this.isEn ? 'Enter email' : '请输入邮箱'
      }[this.registerType]
    },
    registerIcon() {
      if (this.registerType === 'phone') return 'el-icon-mobile-phone'
      if (this.registerType === 'email') return 'el-icon-message'
      return 'el-icon-user'
    }
  },
  watch: {
    registerType() {
      this.form.identifier = ''
      this.form.verificationCode = ''
      this.$nextTick(() => {
        if (this.$refs.registerForm) this.$refs.registerForm.clearValidate()
      })
    }
  },
  beforeDestroy() {
    if (this.codeTimer) clearInterval(this.codeTimer)
  },
  methods: {
    validateIdentifier(rule, value, callback) {
      if (this.registerType === 'username' && !/^[A-Za-z0-9_.-]{3,64}$/.test(value || '')) {
        callback(new Error(this.isEn ? 'Account must be 3-64 characters: letters, numbers, dot, underscore, or hyphen' : '账号长度 3-64 位，仅支持字母、数字、点、下划线、短横线'))
        return
      }
      if (this.registerType === 'phone' && !/^\+?[0-9\s()-]{6,24}$/.test(value || '')) {
        callback(new Error(this.isEn ? 'Invalid phone number' : '手机号格式不正确'))
        return
      }
      if (this.registerType === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value || '')) {
        callback(new Error(this.isEn ? 'Invalid email address' : '邮箱格式不正确'))
        return
      }
      callback()
    },
    validateVerificationCode(rule, value, callback) {
      if (this.needsCode && !/^\d{4,8}$/.test(value || '')) {
        callback(new Error(this.isEn ? 'Enter verification code' : '请输入验证码'))
        return
      }
      callback()
    },
    startCountdown(seconds) {
      this.codeCountdown = seconds
      if (this.codeTimer) clearInterval(this.codeTimer)
      this.codeTimer = setInterval(() => {
        this.codeCountdown -= 1
        if (this.codeCountdown <= 0) clearInterval(this.codeTimer)
      }, 1000)
    },
    sendCode() {
      this.$refs.registerForm.validateField('identifier', async error => {
        if (error) return
        this.sendingCode = true
        try {
          const authBaseUrl = process.env.VUE_APP_AUTH_BASE_URL || 'http://localhost:4201/auth'
          const res = await this.$http.post(`${authBaseUrl}/verification-code`, {
            register_type: this.registerType,
            identifier: this.form.identifier
          })
          this.startCountdown(60)
          const devCode = res.data && res.data.code ? (this.isEn ? `, dev code: ${res.data.code}` : `，开发验证码：${res.data.code}`) : ''
          this.$message.success(`${this.isEn ? 'Verification code sent' : '验证码已发送'}${devCode}`)
        } catch (error) {
          this.$message.error(error.response?.data?.detail || (this.isEn ? 'Failed to send verification code' : '验证码发送失败'))
        } finally {
          this.sendingCode = false
        }
      })
    },
    handleRegister() {
      this.$refs.registerForm.validate(async valid => {
        if (!valid) return

        this.loading = true
        try {
          const authBaseUrl = process.env.VUE_APP_AUTH_BASE_URL || 'http://localhost:4201/auth'
          const identifier = this.form.identifier
          await this.$http.post(`${authBaseUrl}/register`, {
            identifier,
            register_type: this.registerType,
            verification_code: this.needsCode ? this.form.verificationCode : null,
            username: this.registerType === 'username' ? identifier : null,
            phone: this.registerType === 'phone' ? identifier : null,
            email: this.registerType === 'email' ? identifier : null,
            password: this.form.password,
            confirm_password: this.form.confirmPassword
          })
          this.$message.success(this.isEn ? 'Registration successful, please login' : '注册成功，请登录')
          this.$router.push('/login')
        } catch (error) {
          this.$message.error(error.response?.data?.detail || (this.isEn ? 'Registration failed' : '注册失败'))
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 24px;
  background:
    radial-gradient(circle at 18% 20%, rgba(10, 132, 255, 0.2), transparent 35%),
    radial-gradient(circle at 82% 78%, rgba(52, 199, 89, 0.18), transparent 38%),
    #f1f9ff;
}

.auth-card {
  width: 100%;
  max-width: 460px;
  padding: 40px;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(10, 132, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 18px 50px rgba(22, 71, 112, 0.12);
}

.auth-header {
  margin-bottom: 30px;
  text-align: center;
}

.auth-icon {
  font-size: 48px;
  color: #0a84ff;
}

.auth-header h1 {
  margin: 14px 0 8px;
  font-size: 25px;
  color: #10233f;
}

.auth-header p {
  margin: 0;
  color: #607086;
}

.register-type-switch,
.code-row {
  display: flex;
  width: 100%;
}

.register-type-switch >>> .el-radio-button {
  flex: 1 1 0;
  min-width: 0;
}

.register-type-switch >>> .el-radio-button__inner {
  width: 100%;
  height: 40px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: normal;
  line-height: 1.15;
  font-size: 14px;
}

.code-row .el-input {
  flex: 1;
}

.code-button {
  width: 112px;
  margin-left: 10px;
}

.auth-form >>> .el-input__inner {
  height: 46px;
}

.submit-button {
  width: 100%;
  height: 46px;
}

.back-login {
  margin-top: 22px;
  text-align: center;
  color: #607086;
}

.back-login a {
  color: #0a84ff;
  text-decoration: none;
}

@media (max-width: 520px) {
  .auth-card {
    padding: 30px 22px;
  }

  .register-type-switch >>> .el-radio-button__inner {
    padding: 0 6px;
    font-size: 13px;
  }
}
</style>
